/**
 * BrandProfileModal — Verticals + Competitors + SKUs.
 * Full brand profile setup — usable both as onboarding (first visit) and for ongoing edits.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Loader2, Target, Check, AlertCircle, Sparkles, Shield, Package, Plus, X } from 'lucide-react';
import { getVerticals } from '../../services/dsp';
import { useBrandProfile } from '../../contexts/BrandProfileContext';

const CARD = '#121212';
const BORDER = '#1E1E1E';
const BG = '#0A0A0A';
const T = '#00F5FF';
const P = '#FF6BBD';

const SECTION_CLASS = 'space-y-2 pb-5 border-b border-[#1E1E1E] last:border-0';
const HEADER_CLASS = 'flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-gray-400';
const FIELD_CLASS = 'px-3 py-2 rounded-lg text-xs text-white placeholder-gray-600 focus:outline-none';
const FIELD_STYLE = { background: BG, border: `1px solid ${BORDER}` };

const SKU_CATEGORIES = [
  'Hydration', 'Sleep', 'Energy', 'Recovery', 'Beauty', 'Skincare', 'Fitness',
  'Nutrition', 'Apparel', 'Tech', 'Food', 'Beverage', 'Accessories', 'Other',
];

function CompetitorsEditor({ value, onChange }) {
  const [input, setInput] = useState('');
  const add = () => {
    const name = input.trim();
    if (!name || value.includes(name)) return;
    onChange([...value, name]);
    setInput('');
  };
  return (
    <div className="space-y-2" data-testid="competitors-editor">
      <div className="flex gap-2">
        <input type="text" value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Add a competitor brand (e.g., HydrateCo)"
          className={`${FIELD_CLASS} flex-1`} style={FIELD_STYLE}
          data-testid="competitor-input" />
        <button onClick={add} disabled={!input.trim()}
          className="px-3 py-2 rounded-lg text-[11px] font-bold text-black flex items-center gap-1 disabled:opacity-40"
          style={{ background: T }} data-testid="add-competitor-btn">
          <Plus className="h-3 w-3" />Add
        </button>
      </div>
      {value.length === 0 ? (
        <p className="text-[10px] text-gray-600">No competitors configured. We'll show vertical-matched activations until you add some.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5" data-testid="competitor-chips">
          {value.map(c => (
            <span key={c} className="text-[10px] px-2.5 py-1 rounded-full font-semibold flex items-center gap-1.5"
              style={{ background: `${P}15`, color: P, border: `1px solid ${P}35` }}
              data-testid={`competitor-chip-${c}`}>
              {c}
              <button onClick={() => onChange(value.filter(x => x !== c))} className="hover:bg-white/10 rounded-full p-0.5">
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function SkusEditor({ value, onChange }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('Hydration');

  const add = () => {
    const n = name.trim();
    if (!n) return;
    onChange([...value, { name: n, category }]);
    setName('');
  };

  return (
    <div className="space-y-2" data-testid="skus-editor">
      <div className="grid grid-cols-[1fr_140px_auto] gap-2">
        <input type="text" value={name} onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Product / SKU name (e.g., Electrolyte Drink Mix — Berry)"
          className={FIELD_CLASS} style={FIELD_STYLE}
          data-testid="sku-name-input" />
        <select value={category} onChange={e => setCategory(e.target.value)}
          className={FIELD_CLASS} style={FIELD_STYLE} data-testid="sku-category-select">
          {SKU_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <button onClick={add} disabled={!name.trim()}
          className="px-3 py-2 rounded-lg text-[11px] font-bold text-black flex items-center gap-1 disabled:opacity-40"
          style={{ background: T }} data-testid="add-sku-btn">
          <Plus className="h-3 w-3" />Add
        </button>
      </div>
      {value.length === 0 ? (
        <p className="text-[10px] text-gray-600">No products configured yet. Your SKU catalog populates the Build Wave dropdown.</p>
      ) : (
        <div className="space-y-1 max-h-44 overflow-y-auto" data-testid="sku-list">
          {value.map((sku, i) => (
            <div key={`${sku.name}-${i}`} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: BG, border: `1px solid ${BORDER}` }}>
              <Package className="h-3 w-3 text-gray-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold text-white truncate">{sku.name}</p>
                <p className="text-[9px] text-gray-500">{sku.category}</p>
              </div>
              <button onClick={() => onChange(value.filter((_, idx) => idx !== i))}
                className="p-1 text-gray-500 hover:text-red-400"
                data-testid={`remove-sku-${i}`}>
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BrandProfileModal({ open, onOpenChange, isOnboarding = false }) {
  const { profile, save } = useBrandProfile();
  const [verticals, setVerticals] = useState([]);
  const [primary, setPrimary] = useState('');
  const [secondary, setSecondary] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [skus, setSkus] = useState([]);
  const [legalEntityName, setLegalEntityName] = useState('');
  const [legalAddress, setLegalAddress] = useState('');
  const [legalState, setLegalState] = useState('California');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  // Exclude the primary vertical from the secondary list — memoised so we don't
  // re-filter on every re-render of the modal (and re-create the chip array).
  const secondaryOptions = useMemo(
    () => verticals.filter(v => v.vertical !== primary),
    [verticals, primary],
  );

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true); setError(null); setSaved(false);
    setPrimary(profile?.primary_vertical || '');
    setSecondary(profile?.secondary_verticals || []);
    setCompetitors(profile?.competitors || []);
    setSkus(profile?.skus || []);
    setLegalEntityName(profile?.legal_entity_name || '');
    setLegalAddress(profile?.legal_address_line || '');
    setLegalState(profile?.legal_state || 'California');
    getVerticals()
      .then(res => { if (!cancelled) setVerticals(res.verticals || []); })
      .catch(err => { if (!cancelled) setError(err.message || 'Failed to load verticals'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, profile]);

  const toggleSecondary = (v) => {
    if (v === primary) return;
    setSecondary(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  };

  const handleSave = async () => {
    if (!primary) return;
    setSaving(true); setError(null);
    try {
      await save({
        primary_vertical: primary,
        secondary_verticals: secondary.filter(v => v !== primary),
        competitors,
        skus,
        legal_entity_name: legalEntityName.trim(),
        legal_address_line: legalAddress.trim(),
        legal_state: legalState.trim() || 'California',
      });
      setSaved(true);
      setTimeout(() => { onOpenChange(false); setSaved(false); }, 900);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="max-w-3xl text-white" style={{ background: CARD, borderColor: BORDER }} data-testid="brand-profile-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Target className="h-5 w-5" style={{ color: T }} />
            {isOnboarding ? `Welcome${profile?.brand_name ? `, ${profile.brand_name}` : ''}` : 'Brand Profile'}
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            {isOnboarding
              ? 'Tell us about your business so we can personalize moments, competitor intel, and product recommendations.'
              : 'Verticals drive tentpole recommendations, competitors fuel the radar, SKUs populate Build Wave.'}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: T }} />
          </div>
        )}

        {!loading && (
          <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-2">
            {/* ═══ VERTICALS ═══ */}
            <div className={SECTION_CLASS}>
              <div className={HEADER_CLASS}>
                <Sparkles className="h-3 w-3" style={{ color: T }} />Vertical <span className="text-red-400 normal-case">*</span>
                <span className="ml-auto text-[9px] text-gray-600 normal-case font-normal">Powers your personalized tentpole recommendations</span>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-2" data-testid="primary-vertical-grid">
                {verticals.map(v => {
                  const active = primary === v.vertical;
                  return (
                    <button key={v.vertical} onClick={() => {
                      setPrimary(v.vertical);
                      setSecondary(prev => prev.filter(x => x !== v.vertical));
                    }}
                      className="px-3 py-2 rounded-lg text-[11px] font-bold text-left transition-all relative"
                      style={{ background: active ? `${T}15` : BG, color: active ? T : '#aaa', border: `1px solid ${active ? `${T}50` : BORDER}` }}
                      data-testid={`primary-${v.vertical.toLowerCase()}`}>
                      {active && <Check className="absolute top-1.5 right-1.5 h-3 w-3" />}
                      <div>{v.vertical}</div>
                      <div className="text-[9px] font-normal text-gray-500">{v.template_count} moments</div>
                    </button>
                  );
                })}
              </div>
              <p className="text-[9px] uppercase font-bold text-gray-500 pt-1">Secondary verticals (optional)</p>
              <div className="flex flex-wrap gap-1.5" data-testid="secondary-vertical-chips">
                {secondaryOptions.map(v => {
                  const active = secondary.includes(v.vertical);
                  return (
                    <button key={v.vertical} onClick={() => toggleSecondary(v.vertical)}
                      className="px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all"
                      style={{ background: active ? `${P}18` : BG, color: active ? P : '#888', border: `1px solid ${active ? `${P}40` : BORDER}` }}
                      data-testid={`secondary-${v.vertical.toLowerCase()}`}>
                      {active && <Check className="h-2.5 w-2.5 inline mr-1" />}{v.vertical}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ═══ COMPETITORS ═══ */}
            <div className={SECTION_CLASS}>
              <div className={HEADER_CLASS}>
                <Shield className="h-3 w-3" style={{ color: P }} />Competitor Set
                <span className="ml-auto text-[9px] text-gray-600 normal-case font-normal">We track these brands on your Competitor Radar</span>
              </div>
              <CompetitorsEditor value={competitors} onChange={setCompetitors} />
            </div>

            {/* ═══ SKUs ═══ */}
            <div className={SECTION_CLASS}>
              <div className={HEADER_CLASS}>
                <Package className="h-3 w-3" style={{ color: T }} />Product Catalog
                <span className="ml-auto text-[9px] text-gray-600 normal-case font-normal">Populates the SKU dropdown when building a Wave</span>
              </div>
              <SkusEditor value={skus} onChange={setSkus} />
            </div>

            {/* ═══ LEGAL ENTITY (used on auto-generated engagement contracts) ═══ */}
            <div className={SECTION_CLASS}>
              <div className={HEADER_CLASS}>
                <Shield className="h-3 w-3" style={{ color: P }} />Legal Entity
                <span className="ml-auto text-[9px] text-gray-600 normal-case font-normal">Shown on creator engagement contracts (PDFs)</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <input
                  data-testid="legal-entity-name-input"
                  value={legalEntityName}
                  onChange={(e) => setLegalEntityName(e.target.value)}
                  placeholder="Your Brand Inc."
                  className="px-3 py-2 rounded-lg text-xs bg-[#0A0A0A] border outline-none focus:border-[#00D4FF] transition"
                  style={{ borderColor: BORDER, color: '#fff' }}
                />
                <input
                  data-testid="legal-state-input"
                  value={legalState}
                  onChange={(e) => setLegalState(e.target.value)}
                  placeholder="California"
                  className="px-3 py-2 rounded-lg text-xs bg-[#0A0A0A] border outline-none focus:border-[#00D4FF] transition"
                  style={{ borderColor: BORDER, color: '#fff' }}
                />
              </div>
              <input
                data-testid="legal-address-input"
                value={legalAddress}
                onChange={(e) => setLegalAddress(e.target.value)}
                placeholder="1234 Market St, San Francisco, CA 94103"
                className="w-full mt-2 px-3 py-2 rounded-lg text-xs bg-[#0A0A0A] border outline-none focus:border-[#00D4FF] transition"
                style={{ borderColor: BORDER, color: '#fff' }}
              />
              <p className="text-[9px] text-gray-600 mt-1.5 leading-relaxed">
                Optional but recommended. Without these, contracts default to your brand name and California governing law.
              </p>
            </div>

            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-400" /><p className="text-xs text-red-400">{error}</p>
              </div>
            )}

            <button onClick={handleSave} disabled={!primary || saving}
              className="w-full px-5 py-3 rounded-xl text-sm font-bold text-black flex items-center justify-center gap-2 disabled:opacity-40"
              style={{ background: T, boxShadow: `0 0 20px ${T}30` }}
              data-testid="save-profile-btn">
              {saved ? <><Check className="h-4 w-4" />Saved</> : saving ? <><Loader2 className="h-4 w-4 animate-spin" />Saving…</> : <>Save Profile</>}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
